import React, { useState } from 'react';
import { Plus, Search, Download, Trash2, FileText } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { usePagination, useDebounce } from '../hooks/useCustom';
import Table from '../components/Table';
import Badge from '../components/Badge';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';
import { ConfirmModal } from '../components/Modal';
import { Input, Select, FormRow } from '../components/FormComponents';
import Breadcrumb from '../components/Breadcrumb';
import { formatCurrency, formatDate, generateSPKNumber, hondaPrices, filterByMultipleFields, exportToCSV } from '../utils/helpers';

const PAYMENT_METHODS = ['Cash', 'Kredit'];
const TENORS = [12, 24, 36, 48, 60];
const emptyForm = { namaPembeli: '', tipeMobil: '', harga: '', dp: '', metodePembayaran: 'Kredit', tenor: 36, status: 'Proses' };

export default function SPK() {
  const { state, dispatch, showNotification } = useApp();
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const debouncedSearch = useDebounce(search, 300);
  const filtered = filterByMultipleFields(state.spk, debouncedSearch, ['namaPembeli', 'spkNumber', 'tipeMobil']);

  const { currentPage, totalPages, currentData, goToPage } = usePagination(filtered, 10);

  const carOptions = Object.keys(hondaPrices).map(c => ({ value: c, label: c }));

  const handleCarChange = (tipeMobil) => {
    const harga = hondaPrices[tipeMobil] || '';
    const dp = harga ? Math.round(harga * 0.2) : '';
    setForm(f => ({ ...f, tipeMobil, harga, dp }));
  };

  const openAdd = () => { setForm({ ...emptyForm, spkNumber: generateSPKNumber(state.spk.length) }); setEditData(null); setShowModal(true); };
  const openEdit = (s) => { setForm({ ...s }); setEditData(s); setShowModal(true); };

  const handleSubmit = () => {
    if (!form.namaPembeli || !form.tipeMobil) return showNotification('Nama dan tipe mobil wajib diisi', 'error');
    if (editData) {
      dispatch({ type: 'UPDATE_SPK', payload: form });
      showNotification('SPK berhasil diperbarui');
    } else {
      dispatch({ type: 'ADD_SPK', payload: form });
      showNotification('SPK berhasil dibuat');
    }
    setShowModal(false);
  };

  const columns = [
    { key: 'spkNumber', label: 'No. SPK', render: v => <span className="font-mono text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">{v}</span> },
    { key: 'namaPembeli', label: 'Nama Pembeli', sortable: true },
    { key: 'tipeMobil', label: 'Tipe Mobil', sortable: true },
    { key: 'harga', label: 'Harga', render: v => <span className="text-sm font-semibold">{formatCurrency(v)}</span> },
    { key: 'metodePembayaran', label: 'Pembayaran', render: v => <Badge status={v} /> },
    { key: 'tenor', label: 'Tenor', render: (v, row) => row.metodePembayaran === 'Kredit' ? `${v} bln` : '-' },
    { key: 'status', label: 'Status', render: v => <Badge status={v} /> },
    { key: 'createdAt', label: 'Tanggal', render: v => <span className="text-xs text-gray-400">{formatDate(v)}</span> },
    { key: 'actions', label: '', render: (_, row) => (
      <div className="flex gap-1">
        <button onClick={() => openEdit(row)} className="btn-secondary py-1 px-2 text-xs">Edit</button>
        <button onClick={() => setDeleteId(row.id)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"><Trash2 size={14} /></button>
      </div>
    )},
  ];

  // Summary stats
  const totalValue = state.spk.reduce((s, item) => s + (item.harga || 0), 0);
  const completedValue = state.spk.filter(s => s.status === 'Selesai').reduce((s, item) => s + (item.harga || 0), 0);

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'SPK' }]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">SPK (Surat Pesanan Kendaraan)</h1>
          <p className="text-sm text-gray-500">{state.spk.length} SPK total</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportToCSV(state.spk, 'spk')} className="btn-secondary"><Download size={16}/>Export</button>
          <button onClick={openAdd} className="btn-primary"><Plus size={16}/>Buat SPK</button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total SPK', value: state.spk.length, sub: 'semua order' },
          { label: 'Selesai', value: state.spk.filter(s => s.status === 'Selesai').length, sub: formatCurrency(completedValue) },
          { label: 'Dalam Proses', value: state.spk.filter(s => s.status === 'Proses').length, sub: formatCurrency(totalValue - completedValue) },
        ].map((s, i) => (
          <div key={i} className="card text-center">
            <p className="text-xs text-gray-500 uppercase tracking-wider">{s.label}</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{s.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="card">
        <div className="relative max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari SPK, nama, tipe mobil..." className="form-input pl-9" />
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <Table columns={columns} data={currentData} emptyMessage="Belum ada SPK" />
        <div className="px-4 py-3 border-t border-gray-50">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={filtered.length} itemsPerPage={10} />
        </div>
      </div>

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editData ? 'Edit SPK' : 'Buat SPK Baru'}
        size="lg"
        footer={<>
          <button onClick={() => setShowModal(false)} className="btn-secondary">Batal</button>
          <button onClick={handleSubmit} className="btn-primary">{editData ? 'Simpan' : 'Buat SPK'}</button>
        </>}
      >
        <div className="space-y-4">
          <FormRow>
            <Input label="No. SPK" value={form.spkNumber || ''} readOnly className="bg-gray-50" onChange={()=>{}} />
            <Input label="Nama Pembeli *" value={form.namaPembeli} onChange={e => setForm({...form, namaPembeli: e.target.value})} placeholder="Nama lengkap pembeli" />
          </FormRow>
          <Select
            label="Tipe Mobil *"
            value={form.tipeMobil}
            onChange={e => handleCarChange(e.target.value)}
            options={carOptions}
            placeholder="Pilih tipe mobil"
          />
          <FormRow>
            <Input label="Harga (Rp)" value={form.harga ? formatCurrency(form.harga) : ''} readOnly className="bg-gray-50 font-semibold" onChange={()=>{}} />
            <Input label="DP (Rp)" value={form.dp} onChange={e => setForm({...form, dp: e.target.value})} placeholder="Nominal DP" type="number" />
          </FormRow>
          <FormRow>
            <Select label="Metode Pembayaran" value={form.metodePembayaran} onChange={e => setForm({...form, metodePembayaran: e.target.value})} options={PAYMENT_METHODS.map(m => ({ value: m, label: m }))} />
            {form.metodePembayaran === 'Kredit' && (
              <Select label="Tenor" value={form.tenor} onChange={e => setForm({...form, tenor: parseInt(e.target.value)})} options={TENORS.map(t => ({ value: t, label: `${t} bulan` }))} />
            )}
          </FormRow>
          <Select label="Status" value={form.status} onChange={e => setForm({...form, status: e.target.value})} options={[{value:'Proses',label:'Proses'},{value:'Selesai',label:'Selesai'},{value:'Batal',label:'Batal'}]} />
        </div>
      </Modal>

      <ConfirmModal isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={() => { dispatch({ type: 'DELETE_SPK', payload: deleteId }); showNotification('SPK dihapus', 'error'); setDeleteId(null); }} title="Hapus SPK" message="Yakin ingin menghapus SPK ini?" confirmLabel="Hapus" confirmClass="btn-danger" />
    </div>
  );
}
